import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import { PhArrowRight, PhSealCheck } from 'ember-phosphor-icons';
import { buildLpnCardView, lpnBadges } from 'tio-common/utils/lpn/card-view';
import type { LpnListing } from 'tio-common/utils/lpn/listing';
import LpnSchoolLogo from 'tio-employee/components/lpn/school-logo';

interface LpnSchoolRowSignature {
  Args: {
    /** The school as the viewer's company sees it. */
    listing: LpnListing;
    /** Opens the school's detail view. */
    onSelect: (listing: LpnListing) => void;
  };
}

/** Initials for a school without a logo: the first letter of up to four significant words. */
export function schoolInitials(name: string) {
  const words = name
    .split(/\s+/)
    .filter((word) => word && !/^(of|the|at|and|for|&|-)$/i.test(word));
  return words
    .slice(0, 4)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** One school in the TA dashboard drawer: logo, name, headline savings and a details link. */
export default class LpnSchoolRow extends Component<LpnSchoolRowSignature> {
  @service declare intl: IntlService;

  @cached
  get view() {
    return buildLpnCardView(this.args.listing.page, this.args.listing.setting);
  }

  get highlights() {
    const { topBadge, tags } = lpnBadges(this.view, this.intl);
    return [topBadge, ...tags].filter((label): label is string => Boolean(label)).slice(0, 2);
  }

  get subtitle() {
    return [this.view.providedBy?.trim(), ...(this.view.format ?? [])].filter(Boolean).join(' · ');
  }

  get initials() {
    return schoolInitials(this.view.schoolName);
  }

  select = () => this.args.onSelect(this.args.listing);

  <template>
    <article
      class="tio-row rounded-xl border p-3
        {{if @listing.isEmployerRecommended 'tio-border-brand' 'tio-border-default'}}"
      data-test-lpn-school-row
    >
      <div class="tio-col-extra-tight min-w-0 flex-1">
        {{#if @listing.isEmployerRecommended}}
          <Chip @appearance="faded" @intent="primary" @size="sm" class="self-start">
            <PhSealCheck class="tio-icon-xs mr-1 inline" />
            {{t "lpn.schools_drawer.employer_recommended"}}
          </Chip>
        {{/if}}
        <h3 class="tio-text-sm-semibold tio-fg-primary">{{this.view.schoolName}}</h3>
        {{#if this.subtitle}}
          <p class="tio-text-xs-normal tio-fg-muted">{{this.subtitle}}</p>
        {{/if}}
        {{#if this.highlights.length}}
          <div class="tio-cluster gap-1">
            {{#each this.highlights as |label|}}
              <Chip @appearance="faded" @size="sm">{{label}}</Chip>
            {{/each}}
          </div>
        {{/if}}
        <Button
          class="self-start"
          @appearance="outlined"
          @size="sm"
          @label={{t "lpn.schools_drawer.see_details"}}
          @icon={{PhArrowRight}}
          @iconPlacement="end"
          aria-label={{t "lpn.schools_drawer.see_details_for" school=this.view.schoolName}}
          {{on "click" this.select}}
          data-test-lpn-school-row-details
        />
      </div>
      <LpnSchoolLogo @src={{this.view.imageUrl}} @initials={{this.initials}} @size="tile" />
    </article>
  </template>
}
